/**
 * NT msejub player URLs, made embeddable.
 *
 * The NVR publishes its player as plain HTTP on 113.53.239.193:5080. That
 * origin IS publicly reachable — an earlier integration wrongly concluded it
 * was intranet-only — but the dashboard is HTTPS, so browsers refuse to embed
 * it (mixed content) and there is nothing the camera side can do about that.
 *
 * cloudflared publishes the same origin over HTTPS at PLAYER_HTTPS_ORIGIN. The
 * player derives its stream socket from `location.host` and upgrades to `wss`
 * when served over https, so rewriting the page URL is the whole fix: the
 * WebSocket follows automatically. index.html's CSP frame-src must list the
 * same origin, or the iframe is blocked a second time.
 */

export const PLAYER_HTTPS_ORIGIN = "https://cctv-player.nonarkara.org";

const NT_HTTP_ORIGIN = /^http:\/\/113\.53\.239\.193:5080(?=\/|$)/;

/**
 * Rewrite an NT player URL onto the HTTPS origin. Returns null for anything
 * that is not an NT player URL, so a caller can never be handed an arbitrary
 * URL to embed.
 */
export function embeddablePlayerUrl(playerUrl: string | null | undefined): string | null {
  if (!playerUrl || !NT_HTTP_ORIGIN.test(playerUrl)) return null;
  return playerUrl.replace(NT_HTTP_ORIGIN, PLAYER_HTTPS_ORIGIN);
}

/**
 * Stream identity inside an NT player URL:
 *   /pages/player/msejub/<uuid>/<channel>
 */
export interface NtStream {
  uuid: string;
  channel: string;
}

const NT_PLAYER_PATH = /\/pages\/player\/msejub\/([A-Za-z0-9_-]+)\/(\d+)\/?$/;

export function parsePlayerUrl(playerUrl: string | null | undefined): NtStream | null {
  const https = embeddablePlayerUrl(playerUrl);
  if (!https) return null;
  const m = NT_PLAYER_PATH.exec(https);
  return m ? { uuid: m[1], channel: m[2] } : null;
}

/**
 * The MSE WebSocket the NVR streams fragmented MP4 over. This is exactly the
 * URL the vendor's own page builds (it derives the scheme from location, we
 * pin wss because PLAYER_HTTPS_ORIGIN is https).
 *
 * We talk to this socket ourselves rather than embedding the vendor page,
 * because an embedded <video> is subject to Chrome's autoplay policy: the
 * vendor's element is unmuted, so in a fresh profile it stays paused at 0:00
 * behind a play button and the operator sees a black rectangle. Driving the
 * socket ourselves lets us own a muted, autoplaying element that always
 * starts. (This bit me during verification — it "worked" only because my
 * browser had built up Media Engagement on that origin.)
 */
export function mseStreamUrl({ uuid, channel }: NtStream): string {
  const host = PLAYER_HTTPS_ORIGIN.replace(/^https:\/\//, "");
  const q = `uuid=${encodeURIComponent(uuid)}&channel=${encodeURIComponent(channel)}`;
  return `wss://${host}/stream/${uuid}/channel/${channel}/mse?${q}`;
}
